import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LanguageContext = createContext(null);

const STORAGE_KEY = 'rf.lang';
const DEBOUNCE_MS = 300;

/**
 * Regional subpath → (language code, state code) mapping.
 * The mapping powers /keralam, /karnataka, /tamilnadu (and easy future expansion).
 */
export const REGION_MAP = {
  keralam: { lang: 'ml', state: 'KL', display: 'Keralam' },
  karnataka: { lang: 'kn', state: 'KA', display: 'Karnataka' },
  tamilnadu: { lang: 'ta', state: 'TN', display: 'Tamil Nadu' },
  andhra: { lang: 'te', state: 'AP', display: 'Andhra Pradesh' },
  telangana: { lang: 'te', state: 'TS', display: 'Telangana' },
  maharashtra: { lang: 'mr', state: 'MH', display: 'Maharashtra' },
  hindi: { lang: 'hi', state: 'ALL', display: 'Hindi' },
};
export const REGION_SLUGS = Object.keys(REGION_MAP);

const SUPPORTED_LANGS = new Set(['en', 'hi', 'ml', 'kn', 'ta', 'te', 'mr']);

const cacheKeyFor = (lang) => `rf.i18n.${lang}`;
const loadCache = (lang) => {
  try {
    return JSON.parse(localStorage.getItem(cacheKeyFor(lang)) || '{}');
  } catch {
    return {};
  }
};
const saveCache = (lang, map) => {
  try {
    localStorage.setItem(cacheKeyFor(lang), JSON.stringify(map));
  } catch {
    /* quota exceeded — drop silently */
  }
};

// Detect the region slug (if any) from a pathname like '/keralam/events'
export const parseRegionFromPath = (pathname) => {
  const parts = (pathname || '/').split('/').filter(Boolean);
  const first = parts[0]?.toLowerCase();
  return first && REGION_MAP[first] ? first : null;
};

const LanguageInner = ({ children }) => {
  const location = useLocation();
  const regionSlug = parseRegionFromPath(location.pathname);
  const region = regionSlug ? REGION_MAP[regionSlug] : null;

  // Language priority: URL region > user preference in localStorage > 'en'
  const [manualLang, setManualLang] = useState(() => localStorage.getItem(STORAGE_KEY) || 'en');
  const lang = region ? region.lang : manualLang;
  const stateScope = region ? region.state : 'ALL';

  const [dict, setDict] = useState(() => (lang === 'en' ? {} : loadCache(lang)));
  const pendingRef = useRef(new Set());
  const timerRef = useRef(null);

  // Reload dictionary from cache whenever the effective language changes.
  useEffect(() => {
    setDict(lang === 'en' ? {} : loadCache(lang));
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!SUPPORTED_LANGS.has(next)) return;
    localStorage.setItem(STORAGE_KEY, next);
    setManualLang(next);
  }, []);

  const flush = useCallback(async () => {
    const batch = [...pendingRef.current];
    pendingRef.current = new Set();
    if (batch.length === 0 || lang === 'en') return;
    try {
      const { data } = await axios.post(`${API_URL}/api/translations/batch`, {
        texts: batch,
        target_lang: lang,
      });
      const nextDict = { ...loadCache(lang), ...data.translations };
      saveCache(lang, nextDict);
      setDict(nextDict);
    } catch {
      // Re-queue so the next enqueue retries.
      batch.forEach((t) => pendingRef.current.add(t));
    }
  }, [lang]);

  const enqueue = useCallback(
    (text) => {
      if (!text || lang === 'en' || dict[text] !== undefined) return;
      pendingRef.current.add(text);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, DEBOUNCE_MS);
    },
    [dict, flush, lang],
  );

  const t = useCallback(
    (text) => {
      if (lang === 'en' || !text) return text;
      const cached = dict[text];
      if (cached !== undefined) return cached;
      enqueue(text);
      return text;
    },
    [lang, dict, enqueue],
  );

  useEffect(() => {
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, []);

  const value = useMemo(
    () => ({ lang, setLang, t, region, regionSlug, stateScope }),
    [lang, setLang, t, region, regionSlug, stateScope],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const LanguageProvider = ({ children }) => <LanguageInner>{children}</LanguageInner>;

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside LanguageProvider');
  return ctx;
};

/** Convenience hook — returns only the t() function. */
export const useT = () => useLanguage().t;
